import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Modal, Space, Table } from 'antd';

import CreateSubareaModal from '../CreateSubareaModal';
import UpdateSubareaModal from '../UpdateSubareaModal';
import {
  useDeleteSubArea,
  useSubAreaByAreaId,
} from '@/core/domain/operational/SubAreas/SubAreas.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import LoadMoreSummary from '@/components/_common/LoadMoreSummary';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { ColumnsType } from 'antd/lib/table';
import { SubArea } from '@/core/domain/operational/SubAreas/SubAreas.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const SubareasModule = () => {
  const intl = useIntl();
  const { areaId } = useRouteMatch<{ areaId: string }>().params;

  const [createSubareaModalVisible, setCreateSubareaModalVisible] =
    useState(false);
  const [updateSubareaModalVisible, setUpdateSubareaModalVisible] =
    useState(false);

  const [selectedSubarea, setSelectedSubarea] = useState<SubArea>();

  const {
    data: areasData,
    isFetching: isFetchingSubAreas,
    hasNextPage,
    fetchNextPage,
  } = useSubAreaByAreaId({ id: Number(areaId) });

  const subAreas = flatInfiniteData(areasData);

  const removeMutation = useDeleteSubArea();

  const handleCreateSubareaModalVisible = () => {
    setCreateSubareaModalVisible(!createSubareaModalVisible);
  };

  const handleUpdateSubareaModalVisible = (areas?: SubArea) => {
    if (updateSubareaModalVisible) {
      setSelectedSubarea(undefined);
    } else {
      setSelectedSubarea(areas);
    }

    setUpdateSubareaModalVisible(!updateSubareaModalVisible);
  };

  function handleRemoveSubareaClick(subarea: SubArea) {
    Modal.confirm({
      title: 'Remover Sub-área',
      content: `Deseja remover a sub-área "${subarea?.name}"`,
      onOk: async () => {
        try {
          // Fetch Service to remove area item.
          await removeMutation.mutateAsync(subarea.id);
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'areas.modal.remove.button.ok',
      }),
      okType: 'danger',
    });
  }

  const subAreaColumns: ColumnsType<SubArea> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'areas.subareas.description' }),
      ellipsis: true,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 100,
      align: 'right',
      render: (areas: SubArea) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => handleUpdateSubareaModalVisible(areas),
            }}
            disabled={!hasPermissions([Authorities.REGISTER_SUBAREA_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveSubareaClick(areas)}
            disabled={!hasPermissions([Authorities.REGISTER_SUBAREA_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="create-page-title" style={{ marginTop: '32px' }}>
        <SpaceForButton justifyContent="space-between">
          <h2>
            <FormattedMessage id="areas.subareas" />
          </h2>

          <div>
            <Button
              block
              type="primary"
              onClick={handleCreateSubareaModalVisible}
              disabled={!hasPermissions([Authorities.REGISTER_SUBAREA_ADD])}
            >
              <FormattedMessage id="areas.subareas.create" />
            </Button>
          </div>
        </SpaceForButton>
      </div>

      <Table
        size="middle"
        rowKey={(row) => row.id}
        loading={isFetchingSubAreas}
        dataSource={subAreas}
        columns={subAreaColumns}
        pagination={false}
        summary={() =>
          hasNextPage ? (
            <LoadMoreSummary
              onClick={fetchNextPage}
              columnsCount={subAreaColumns.length}
            />
          ) : null
        }
      />

      <CreateSubareaModal
        visible={createSubareaModalVisible}
        handleVisible={handleCreateSubareaModalVisible}
      />

      <UpdateSubareaModal
        visible={updateSubareaModalVisible}
        handleVisible={handleUpdateSubareaModalVisible}
        subarea={selectedSubarea}
      />
    </>
  );
};

export default SubareasModule;
